/**
 * 콘솔 페이지가 사용하는 cloud-api 호출들. axios 기반(api-client.ts)
 * 위에 얇은 어댑터.
 *
 * 응답 타입은 swagger 의 DTO 기준 핵심 필드만 추림 — 추가 필드가 와도 axios
 * 가 무시하므로 호환 안전.
 */
import { apiClient } from "./api-client";

export type MemberRole = "OWNER" | "ADMIN" | "MEMBER" | "VIEWER";
export type OrgPlan = "PERSONAL" | "TEAM" | "ENTERPRISE" | string;

export interface OrganizationWithRole {
  id: string;
  name: string;
  slug: string;
  plan: OrgPlan;
  role: MemberRole;
  createdAt: string;
  updatedAt: string;
}

export interface MemberAccount {
  id: string;
  email: string;
  name: string | null;
}

export interface Member {
  id: string;
  organizationId: string;
  role: MemberRole;
  account: MemberAccount;
  createdAt: string;
}

export type CommitStrategy =
  | "PER_TASK_PR"
  | "PER_PLAN_MULTI_COMMIT"
  | "PER_PLAN_SINGLE_COMMIT";

export interface Project {
  id: string;
  organizationId: string;
  name: string;
  slug: string;
  gitUrl: string;
  defaultBranch: string;
  defaultProvider: string | null;
  defaultModel: string | null;
  commitStrategy: CommitStrategy;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProjectInput {
  organizationId: string;
  name: string;
  slug: string;
  gitUrl?: string;
  defaultBranch?: string;
  commitStrategy?: CommitStrategy;
}

export interface UpdateProjectInput {
  version: number;
  name?: string;
  slug?: string;
  gitUrl?: string;
  defaultBranch?: string;
  commitStrategy?: CommitStrategy;
}

export interface ProjectListResponse {
  items: Project[];
  total: number;
}

/**
 * cloud-api 의 paginated 응답 형식 — { items, meta: { total, page, pageSize, totalPages } }.
 * UI 는 단순한 { items, total } 만 필요하므로 어댑터에서 normalize.
 */
interface PaginatedRaw<T> {
  items: T[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

function normalizePaginated<T>(raw: PaginatedRaw<T>): { items: T[]; total: number } {
  return { items: raw.items, total: raw.meta?.total ?? 0 };
}

export async function listOrganizations(): Promise<OrganizationWithRole[]> {
  const { data } = await apiClient.get<OrganizationWithRole[]>("/organizations");
  return data;
}

export async function listMembers(organizationId: string): Promise<Member[]> {
  const { data } = await apiClient.get<Member[]>(
    `/organizations/${organizationId}/members`,
  );
  return data;
}

export async function listProjects(
  organizationId?: string,
): Promise<ProjectListResponse> {
  const { data } = await apiClient.get<PaginatedRaw<Project>>("/projects", {
    params: organizationId ? { organizationId } : {},
  });
  return normalizePaginated(data);
}

export async function createProject(input: CreateProjectInput): Promise<Project> {
  const { data } = await apiClient.post<Project>("/projects", input);
  return data;
}

export async function updateProject(
  id: string,
  input: UpdateProjectInput,
): Promise<Project> {
  const { data } = await apiClient.patch<Project>(`/projects/${id}`, input);
  return data;
}

export async function deleteProject(id: string): Promise<void> {
  await apiClient.delete(`/projects/${id}`);
}

export async function changePassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  await apiClient.post("/auth/change-password", input);
}

export async function createOrganization(input: {
  name: string;
  slug: string;
  plan?: OrgPlan;
}): Promise<OrganizationWithRole> {
  const { data } = await apiClient.post<OrganizationWithRole>(
    "/organizations",
    input,
  );
  return data;
}

export async function deleteOrganization(id: string): Promise<void> {
  await apiClient.delete(`/organizations/${id}`);
}

export interface UsageSummaryTotals {
  count: number;
  totalEstimatedCost: string;
  totalDurationMs: number;
  totalInputTokens: number;
  totalOutputTokens: number;
}

export interface UsageProviderBucket {
  provider: string | null;
  count: number;
  totalDurationMs: number;
  totalInputTokens: number;
  totalOutputTokens: number;
}

export interface UsageProjectBucket {
  projectId: string | null;
  count: number;
  totalEstimatedCost: string;
  totalDurationMs: number;
}

export interface UsageAccountBucket {
  accountId: string | null;
  count: number;
  totalDurationMs: number;
}

export interface UsageSummary {
  totals: UsageSummaryTotals;
  byProject: UsageProjectBucket[];
  byProvider: UsageProviderBucket[];
  byAccount: UsageAccountBucket[];
}

export async function getUsageSummary(params?: {
  organizationId?: string;
  projectId?: string;
}): Promise<UsageSummary> {
  const { data } = await apiClient.get<UsageSummary>("/usage-logs/summary", {
    params,
  });
  return data;
}

export interface ProviderRateLimitStatus {
  provider: string;
  rateLimitedCount: number;
  lastOccurredAt: string;
}

export async function getProviderStatus(
  organizationId: string,
): Promise<ProviderRateLimitStatus[]> {
  const { data } = await apiClient.get<ProviderRateLimitStatus[]>(
    `/organizations/${organizationId}/providers/status`,
  );
  return data;
}

export interface ProjectTask {
  id: string;
  type: string;
  status: string;
  title: string;
  priority: string;
  createdAt: string;
  updatedAt: string;
}

export async function listProjectTasks(
  projectId: string,
  params?: { page?: number; pageSize?: number; status?: string; q?: string },
): Promise<{ items: ProjectTask[]; total: number }> {
  const { data } = await apiClient.get<PaginatedRaw<ProjectTask>>(
    `/projects/${projectId}/tasks`,
    { params },
  );
  return normalizePaginated(data);
}

export interface ProjectPullRequest {
  id: string;
  providerPrId: string;
  status: string;
  title: string;
  url: string;
  createdAt: string;
  mergedAt: string | null;
}

export async function listProjectPullRequests(
  projectId: string,
  params?: { page?: number; pageSize?: number; status?: string; q?: string },
): Promise<{ items: ProjectPullRequest[]; total: number }> {
  const { data } = await apiClient.get<PaginatedRaw<ProjectPullRequest>>(
    `/projects/${projectId}/pull-requests`,
    { params },
  );
  return normalizePaginated(data);
}

// ── Releases ───────────────────────────────────────────────────────────────

export interface Release {
  id: string;
  version: string;
  channel: string;
  notes: string | null;
  macArm64Url: string | null;
  macX64Url: string | null;
  windowsUrl: string | null;
  published: boolean;
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateReleaseInput {
  version: string;
  channel?: string;
  notes?: string;
  macArm64Url?: string;
  macX64Url?: string;
  windowsUrl?: string;
  published?: boolean;
}

export interface UpdateReleaseInput {
  channel?: string;
  notes?: string | null;
  macArm64Url?: string | null;
  macX64Url?: string | null;
  windowsUrl?: string | null;
  published?: boolean;
}

export async function listPublicReleases(): Promise<Release[]> {
  const { data } = await apiClient.get<Release[]>("/releases");
  return data;
}

export async function listAdminReleases(): Promise<Release[]> {
  const { data } = await apiClient.get<Release[]>("/releases/admin/all");
  return data;
}

export async function createRelease(
  input: CreateReleaseInput,
): Promise<Release> {
  const { data } = await apiClient.post<Release>("/releases", input);
  return data;
}

export async function updateRelease(
  id: string,
  input: UpdateReleaseInput,
): Promise<Release> {
  const { data } = await apiClient.patch<Release>(`/releases/${id}`, input);
  return data;
}

export async function deleteRelease(id: string): Promise<void> {
  await apiClient.delete(`/releases/${id}`);
}

// ── Pricing Plans ──────────────────────────────────────────────────────────

export interface PricingPlan {
  id: string;
  slug: string;
  name: string;
  priceCents: number;
  currency: string;
  billingPeriod: string;
  isPerSeat: boolean;
  description: string | null;
  features: string[];
  sortOrder: number;
  active: boolean;
  highlighted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePricingPlanInput {
  slug: string;
  name: string;
  priceCents: number;
  currency?: string;
  billingPeriod?: string;
  isPerSeat?: boolean;
  description?: string;
  features?: string[];
  sortOrder?: number;
  active?: boolean;
  highlighted?: boolean;
}

export interface UpdatePricingPlanInput {
  name?: string;
  priceCents?: number;
  currency?: string;
  billingPeriod?: string;
  isPerSeat?: boolean;
  description?: string | null;
  features?: string[];
  sortOrder?: number;
  active?: boolean;
  highlighted?: boolean;
}

export async function listPublicPlans(): Promise<PricingPlan[]> {
  const { data } = await apiClient.get<PricingPlan[]>("/pricing-plans");
  return data;
}

export async function listAdminPlans(): Promise<PricingPlan[]> {
  const { data } = await apiClient.get<PricingPlan[]>(
    "/pricing-plans/admin/all",
  );
  return data;
}

export async function createPricingPlan(
  input: CreatePricingPlanInput,
): Promise<PricingPlan> {
  const { data } = await apiClient.post<PricingPlan>("/pricing-plans", input);
  return data;
}

export async function updatePricingPlan(
  id: string,
  input: UpdatePricingPlanInput,
): Promise<PricingPlan> {
  const { data } = await apiClient.patch<PricingPlan>(
    `/pricing-plans/${id}`,
    input,
  );
  return data;
}

export async function deletePricingPlan(id: string): Promise<void> {
  await apiClient.delete(`/pricing-plans/${id}`);
}

// ── Admin (SuperAdmin only) ─────────────────────────────────────────────────

export interface AdminUserListItem {
  id: string;
  email: string;
  name: string | null;
  emailVerified: boolean;
  suspendedAt: string | null;
  isSuperAdmin: boolean;
  organizationCount: number;
  createdAt: string;
}

export interface AdminUserMembership {
  organizationId: string;
  organizationName: string;
  organizationSlug: string;
  role: MemberRole;
  joinedAt: string;
}

export interface AdminUserDetail extends AdminUserListItem {
  memberships: AdminUserMembership[];
  activeSessions: number;
}

export async function listAdminUsers(params?: {
  page?: number;
  pageSize?: number;
  q?: string;
}): Promise<{ items: AdminUserListItem[]; total: number; pageSize: number }> {
  const { data } = await apiClient.get<PaginatedRaw<AdminUserListItem>>(
    "/admin/users",
    { params },
  );
  return {
    items: data.items,
    total: data.meta?.total ?? 0,
    pageSize: data.meta?.pageSize ?? 30,
  };
}

export async function getAdminUserDetail(id: string): Promise<AdminUserDetail> {
  const { data } = await apiClient.get<AdminUserDetail>(`/admin/users/${id}`);
  return data;
}

export async function adminVerifyEmail(id: string): Promise<void> {
  await apiClient.post(`/admin/users/${id}/verify-email`);
}

export async function adminRevokeSessions(id: string): Promise<{ revokedCount: number }> {
  const { data } = await apiClient.post<{ revokedCount: number }>(
    `/admin/users/${id}/revoke-sessions`,
  );
  return data;
}

export async function adminSuspend(id: string): Promise<void> {
  await apiClient.post(`/admin/users/${id}/suspend`);
}

export async function adminUnsuspend(id: string): Promise<void> {
  await apiClient.post(`/admin/users/${id}/unsuspend`);
}

export async function adminToggleSuperAdmin(
  id: string,
  grant: boolean,
): Promise<{ isSuperAdmin: boolean }> {
  const { data } = await apiClient.post<{ isSuperAdmin: boolean }>(
    `/admin/users/${id}/super-admin`,
    { grant },
  );
  return data;
}

// ── Invitations ────────────────────────────────────────────────────────────

export interface Invitation {
  id: string;
  organizationId: string;
  organizationName: string;
  email: string;
  role: MemberRole;
  inviterName: string | null;
  expiresAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface InvitationPreview {
  organizationName: string;
  email: string;
  role: MemberRole;
  inviterName: string | null;
  expiresAt: string;
  acceptable: boolean;
  reason?: string;
}

export async function listInvitations(
  organizationId: string,
): Promise<Invitation[]> {
  const { data } = await apiClient.get<Invitation[]>(
    `/organizations/${organizationId}/invitations`,
  );
  return data;
}

export async function createInvitation(
  organizationId: string,
  input: { email: string; role?: MemberRole },
): Promise<Invitation> {
  const { data } = await apiClient.post<Invitation>(
    `/organizations/${organizationId}/invitations`,
    input,
  );
  return data;
}

export async function revokeInvitation(
  organizationId: string,
  invitationId: string,
): Promise<void> {
  await apiClient.delete(
    `/organizations/${organizationId}/invitations/${invitationId}`,
  );
}

export async function previewInvitation(
  token: string,
): Promise<InvitationPreview> {
  const { data } = await apiClient.get<InvitationPreview>(
    `/invitations/by-token/${encodeURIComponent(token)}`,
  );
  return data;
}

export async function acceptInvitation(
  token: string,
): Promise<{ organizationId: string; organizationName: string }> {
  const { data } = await apiClient.post<{
    organizationId: string;
    organizationName: string;
  }>(`/invitations/${encodeURIComponent(token)}/accept`);
  return data;
}
